import { MutableRefObject, useCallback, useState } from 'react';

import {
  FormEntry,
  SaveSuccess,
  SubmitAllResult,
} from '@/data/context/QuestionFormsOrchestrator';
import { compact } from 'lodash';

export type UseSubmitProps = {
  registry: MutableRefObject<Map<string, FormEntry>>;
  onSuccess: (...identifiers: string[]) => void;
  onFail: (...identifiers: string[]) => void;
  onSaved?: (result: SubmitAllResult) => void;
};

const useSubmit = ({
  registry,
  onSuccess,
  onFail,
  onSaved,
}: UseSubmitProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = useCallback(
    async (identifier: string, entry: FormEntry) => {
      try {
        const result = await entry.submit();
        onSuccess(identifier);
        return { identifier, result };
      } catch {
        onFail(identifier);
        return null;
      }
    },
    [onSuccess, onFail],
  );

  const processForm = useCallback(
    async (identifier: string): Promise<SaveSuccess | null> => {
      const entry = registry.current.get(identifier);
      if (!entry?.hasChanges) return null;
      const valid = await entry.validate();
      if (!valid) {
        onFail(identifier);
        return null;
      }
      return onSubmit(identifier, entry);
    },
    [registry, onSubmit, onFail],
  );

  const submitForm = useCallback(
    async (identifier: string) => {
      const success = await processForm(identifier);
      if (success) onSaved?.({ successes: [success], failedIdentifiers: [] });
    },
    [processForm, onSaved],
  );

  const getDirtyIdentifiers = useCallback(() => {
    const entries = [...registry.current.entries()];
    const dirtyEntries = entries.filter(([, e]) => e.hasChanges);
    return dirtyEntries.map(([id]) => id);
  }, [registry]);

  const submitAll = useCallback(async (): Promise<SubmitAllResult> => {
    setIsSaving(true);
    try {
      const dirtyIds = getDirtyIdentifiers();

      const results = await Promise.all(dirtyIds.map(processForm));
      const successes = compact(results);
      const failedIdentifiers = dirtyIds.filter((_, index) => !results[index]);

      if (successes.length > 0) onSaved?.({ successes, failedIdentifiers });

      return { successes, failedIdentifiers };
    } finally {
      setIsSaving(false);
    }
  }, [processForm, onSaved, getDirtyIdentifiers]);

  return { submitForm, submitAll, isSaving };
};

export default useSubmit;
